import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { CategoryItem, SlideshowItem, GalleryState, CategoryType } from '../types';
import { categoryApi, slideshowApi } from '../services/api';
import { fetchContentVersion, setContentVersionForSession } from '../services/contentVersion';
import { cacheClearAll } from '../services/cacheManager';

interface DataContextType extends GalleryState {
    slideshow: SlideshowItem[];
    refreshCategories: () => Promise<void>;
    refreshSlideshow: () => Promise<void>;
    updateCategory: (id: number | string, data: Partial<CategoryItem>) => Promise<boolean>;
    deleteCategory: (id: number | string) => Promise<boolean>;
}

const DataContext = createContext<DataContextType>({
    categories: {} as Record<CategoryType, CategoryItem[]>,
    slideshow: [],
    loading: false,
    error: null,
    refreshCategories: async () => { },
    refreshSlideshow: async () => { },
    updateCategory: async () => false,
    deleteCategory: async () => false,
});

export const useData = () => useContext(DataContext);

interface DataProviderProps {
    children: ReactNode;
}

export const DataProvider: React.FC<DataProviderProps> = ({ children }) => {
    const [categories, setCategories] = useState<Record<CategoryType, CategoryItem[]>>({} as Record<CategoryType, CategoryItem[]>);
    const [slideshow, setSlideshow] = useState<SlideshowItem[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const refreshCategories = async () => {
        setLoading(true);
        setError(null);

        try {
            // Use non-paginated version to avoid conflict with infinite scroll
            const { data, error: apiError } = await categoryApi.getAllNoPagination();

            if (apiError) {
                setError(apiError);
                return;
            }

            if (data?.data) {
                // Group categories by type
                const grouped: Record<CategoryType, CategoryItem[]> = {} as Record<CategoryType, CategoryItem[]>;

                data.data.forEach((item: CategoryItem) => {
                    if (!grouped[item.type]) {
                        grouped[item.type] = [];
                    }
                    grouped[item.type].push(item);
                });

                setCategories(grouped);
            }
        } catch (err) {
            setError('Failed to fetch categories'); // TODO: Add translation
            console.error('Error fetching categories:', err);
        } finally {
            setLoading(false);
        }
    };

    const refreshSlideshow = async () => {
        try {
            const { data, error: apiError } = await slideshowApi.getAll();

            if (apiError) {
                setError(apiError);
                return;
            }

            if (data?.data) {
                setSlideshow(data.data);
            }
        } catch (err) {
            setError('Failed to fetch slideshow');
            console.error('Error fetching slideshow:', err);
        }
    };

    const updateCategory = async (id: number | string, updateData: Partial<CategoryItem>): Promise<boolean> => {
        try {
            const { data, error: apiError } = await categoryApi.update(id, updateData);

            if (apiError) {
                setError(apiError);
                return false;
            }

            if (data?.data) {
                // Update local state
                setCategories(prev => {
                    const updated = { ...prev };
                    const productType = data.data.type;

                    if (updated[productType]) {
                        const index = updated[productType].findIndex(item => item.id === id);
                        if (index !== -1) {
                            updated[productType][index] = data.data;
                        }
                    }

                    return updated;
                });

                return true;
            }

            return false;
        } catch (err) {
            setError('Failed to update category');
            console.error('Error updating category:', err);
            return false;
        }
    };

    const deleteCategory = async (id: number | string): Promise<boolean> => {
        try {
            const { error: apiError } = await categoryApi.delete(id);

            if (apiError) {
                setError(apiError);
                return false;
            }

            // Remove from local state
            setCategories(prev => {
                const updated = { ...prev };
                Object.keys(updated).forEach(type => {
                    updated[type as CategoryType] = updated[type as CategoryType].filter(item => item.id !== id);
                });
                return updated;
            });

            return true;
        } catch (err) {
            setError('Failed to delete category');
            console.error('Error deleting category:', err);
            return false;
        }
    };

    // Load initial data
    useEffect(() => {
        // Only load slideshow data automatically
        refreshSlideshow();

        // Categories can be loaded on-demand by calling refreshCategories()
        // This avoids conflict with infinite scroll components
    }, []);

    // Lightweight poller to detect Strapi content version changes in the background
    useEffect(() => {
        let stop = false;
        const interval = Number(import.meta.env.VITE_CACHE_VERSION_POLL_MS || 60000);

        const tick = async () => {
            try {
                const latest = await fetchContentVersion();
                if (!latest) return; // Express API unavailable; skip

                const prevRaw = sessionStorage.getItem('contentVersion');
                const prev = prevRaw ? Number(prevRaw) : null;
                if (prev !== latest.version) {
                    // Version changed → clear local cache and refresh critical data
                    await cacheClearAll();
                    sessionStorage.setItem('contentVersion', String(latest.version));
                    setContentVersionForSession(latest);

                    // Refresh key datasets; leave categories to on-demand fetch
                    refreshSlideshow();

                    // Optional: let listeners know
                    try { window.dispatchEvent(new CustomEvent('content-version-updated', { detail: latest })); } catch { }
                }
            } catch {
                // ignore errors; will retry on next tick
            }
        };

        // Kick off immediately, then at interval
        tick();
        const id = window.setInterval(() => { if (!stop) tick(); }, interval);
        return () => { stop = true; window.clearInterval(id); };
    }, [refreshSlideshow]);

    const contextValue: DataContextType = {
        categories,
        slideshow,
        loading,
        error,
        refreshCategories,
        refreshSlideshow,
        updateCategory,
        deleteCategory,
    };

    return (
        <DataContext.Provider value={contextValue}>
            {children}
        </DataContext.Provider>
    );
};